import { Col, ListGroup, Modal, Row, Tab, Table, Tabs } from "react-bootstrap";
import { callQuery, HOME_MEDICATIONS_BY_MRN, HomeMedication, Medication } from '../../amplify/appsync/query';
import { PatientVisit } from "../../amplify/appsync/search-patients";

import './Meds.css';
import { getHourMinuteDifference } from "../../utils/time";
import { useEffect, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPills } from "@fortawesome/free-solid-svg-icons";

/**
 * Modal showing meds information
 */
export const MedsModal = (props: { 
    showModal: boolean,
    setShowModal: any,
    meds: Medication[],
    patientVisit: PatientVisit,
    addAnnotationToPlots: any,
}) => {

    const [currentTab, setCurrentTab] = useState<any>("administration");
    const [homeMeds, setHomeMeds] = useState<any>([]);

    useEffect(() => {
        if (currentTab === "home") {
            callQuery({
                operation: HOME_MEDICATIONS_BY_MRN,
                mrn: props.patientVisit.mrn,
            })
            .then((meds: HomeMedication[]) => {
                setHomeMeds(meds);
            });
        }
    }, [currentTab]);

    return (
        <Modal show={props.showModal} dialogClassName="modal-90w" onHide={() => props.setShowModal(false)}>
            <Modal.Header closeButton>
                <Modal.Title>Medications</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <Tabs
                    activeKey={currentTab}
                    onSelect={(k) => setCurrentTab(k ?? "")}
                    className="mb-3"
                >
                    <Tab eventKey="administration" title="Administered During ED Visit">
                        <Row>
                            <Col md={12}>
                                {props.meds.length > 0 ? (
                                    <Table className="med-group-table">
                                        <thead>
                                            <tr>
                                                <th>Administered Time</th>
                                                <th>End Time</th>
                                                <th>Duration</th>
                                                <th>Medication Name</th>
                                                <th>Action</th>
                                                <th>Dose</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {props.meds.map((med: Medication) => (
                                                <tr key={med.med_name + med.action_time}
                                                    onClick={() => {
                                                        const medEnd = med.end_time ? new Date(med.end_time).getTime() / 1000 : (new Date(med.action_time).getTime() / 1000 + 1);
                                                        const medLength = medEnd - new Date(med.action_time).getTime() / 1000;
                                                        props.addAnnotationToPlots(new Date(med.action_time).getTime() / 1000, props.patientVisit, "eventMark", med.med_name, medLength);
                                                        props.setShowModal(false);
                                                    }}
                                                >
                                                    <td>
                                                        {new Date(med.action_time).toLocaleString()}
                                                    </td>
                                                    <td>
                                                        {
                                                            med.action_time !== med.end_time ? (
                                                                <>
                                                                    {new Date(med.end_time).toLocaleString()}
                                                                </>
                                                            ) : (
                                                                <>-</>
                                                            )
                                                        }
                                                    </td>
                                                    <td>
                                                        {
                                                            med.action_time !== med.end_time ? (
                                                                <>
                                                                    {getHourMinuteDifference(new Date(med.action_time), new Date(med.end_time))}
                                                                </>
                                                            ) : (
                                                                <>-</>
                                                            )
                                                        }
                                                    </td>
                                                    <td>
                                                        <strong>{med.med_name}</strong>
                                                    </td>
                                                    <td>
                                                        {med.action}
                                                    </td>
                                                    <td>
                                                        {med.dose} {med.dose_unit}
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </Table>
                                ) : (
                                    <div style={{
                                        textAlign: 'center',
                                        padding: '60px 20px',
                                        color: '#6c757d'
                                    }}>
                                        <div style={{ fontSize: '3em', marginBottom: '16px' }}><FontAwesomeIcon icon={faPills} /></div>
                                        <h5 style={{ marginBottom: '8px' }}>No Medications Administered</h5>
                                        <p style={{ fontSize: '0.9em', marginBottom: 0 }}>
                                            No medications have been administered during this ED visit yet.
                                        </p>
                                    </div>
                                )}
                            </Col>
                        </Row>
                    </Tab>
                    <Tab eventKey="home" title="Home Medications">
                        {homeMeds.length > 0 ? (
                            <Table>
                                <thead>
                                    <tr>
                                        <th>Medication Name</th>
                                        <th>Route</th>
                                        <th>Instructions</th>
                                        <th>Status</th>
                                        <th>Start Date</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {homeMeds.map((med: HomeMedication, idx: number) => (
                                        <tr key={`${med.med_name}-${idx}`}>
                                            <td>
                                                <strong>{med.med_name}</strong>
                                                {med.atc_code && (
                                                    <div style={{ fontSize: '0.85em', color: '#666' }}>
                                                        ATC: {med.atc_code}
                                                    </div>
                                                )}
                                            </td>
                                            <td>
                                                {med.route || "-"}
                                            </td>
                                            <td>
                                                {med.sig && (
                                                    <div style={{ maxWidth: '300px' }}>
                                                        {med.sig}
                                                    </div>
                                                )}
                                                {med.as_needed && (
                                                    <div style={{ fontSize: '0.85em', color: '#666', marginTop: '4px' }}>
                                                        <em>PRN: {med.as_needed}</em>
                                                    </div>
                                                )}
                                                {med.patient_instruction && (
                                                    <div style={{ fontSize: '0.85em', color: '#666', marginTop: '4px' }}>
                                                        {med.patient_instruction}
                                                    </div>
                                                )}
                                                {!med.sig && !med.as_needed && !med.patient_instruction && "-"}
                                            </td>
                                            <td>
                                                <span style={{
                                                    padding: '2px 8px',
                                                    borderRadius: '4px',
                                                    fontSize: '0.85em',
                                                    backgroundColor: med.status === 'active' ? '#d4edda' : '#f8d7da',
                                                    color: med.status === 'active' ? '#155724' : '#721c24'
                                                }}>
                                                    {med.status || 'unknown'}
                                                </span>
                                            </td>
                                            <td>
                                                {med.start_date ? new Date(med.start_date).toLocaleDateString() : "-"}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </Table>
                        ) : (
                            <div style={{
                                textAlign: 'center',
                                padding: '60px 20px',
                                color: '#6c757d'
                            }}>
                                <div style={{ fontSize: '3em', marginBottom: '16px' }}><FontAwesomeIcon icon={faPills} /></div>
                                <h5 style={{ marginBottom: '8px' }}>No Home Medications Found</h5>
                                <p style={{ fontSize: '0.9em', marginBottom: 0 }}>
                                    This patient does not have any active home medications on record.
                                </p>
                            </div>
                        )}
                    </Tab>
                </Tabs>
            </Modal.Body>
        </Modal>
    );
};
